/**
 * DigitalCenterPreview — la ilustración del hero de la landing.
 *
 * No es una foto de stock ni una imagen generada: reconstruye, con los
 * mismos tokens y la misma tipografía del sistema real, cómo se ve
 * honestamente el Digital Center (panel de cola con SLA, hilo de mensajes
 * estilo WhatsApp, pill de presencia). Mockup aprobado 2026-08-17 — ver
 * TASKS.md del cockpit.
 */

const QUEUE: Array<{ name: string; channel: string; preview: string; active: boolean }> = [
  { name: "María G.", channel: "WA", preview: "¿Ya está listo mi pedido?", active: true },
  { name: "Carlos R.", channel: "MS", preview: "Buenas, quisiera cotizar", active: false },
  { name: "Lucía P.", channel: "WA", preview: "Perfecto, gracias!", active: false },
];

function Bubble({ from, text, time }: { from: "customer" | "agent"; text: string; time: string }) {
  const isAgent = from === "agent";
  return (
    <div className={`mb-2 flex flex-col ${isAgent ? "items-end" : "items-start"}`}>
      <div
        className="max-w-[78%] rounded-[var(--radius-lg)] px-3 py-2 text-[12.5px] leading-snug"
        style={
          isAgent
            ? { background: "var(--brand)", color: "var(--brand-on)", fontWeight: 500, borderBottomRightRadius: 4 }
            : { background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text-1)", borderBottomLeftRadius: 4 }
        }
      >
        {text}
      </div>
      <span className="mt-0.5 text-[9.5px]" style={{ color: "var(--text-3)" }}>
        {time}
      </span>
    </div>
  );
}

export function DigitalCenterPreview() {
  return (
    <div className="relative" style={{ transform: "rotate(0.6deg)" }}>
      <div
        className="overflow-hidden rounded-[var(--radius-2xl)] border"
        style={{ borderColor: "var(--border)", background: "var(--surface)", boxShadow: "var(--elevation-lg)" }}
        role="img"
        aria-label="Vista de ejemplo del Digital Center de WASOM"
      >
        {/* barra superior */}
        <div
          className="flex items-center justify-between border-b px-4 py-3"
          style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}
        >
          <div className="flex gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: "var(--border)" }} />
            <span className="h-2 w-2 rounded-full" style={{ background: "var(--border)" }} />
            <span className="h-2 w-2 rounded-full" style={{ background: "var(--border)" }} />
          </div>
          <span
            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold"
            style={{ borderColor: "var(--border)", color: "var(--text-1)" }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--status-good)" }} />
            Disponible
          </span>
        </div>

        {/* cuerpo: cola + hilo */}
        <div className="grid" style={{ gridTemplateColumns: "168px 1fr", minHeight: 360 }}>
          <div className="border-r p-2.5" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
            <div
              className="px-1.5 pb-2 pt-1 text-[10px] font-bold uppercase tracking-wide"
              style={{ color: "var(--text-3)" }}
            >
              Cola · Soporte
            </div>
            {QUEUE.map((row) => (
              <div
                key={row.name}
                className="mb-0.5 flex flex-col gap-0.5 rounded-[var(--radius-md)] px-2 py-2"
                style={row.active ? { background: "var(--surface)", boxShadow: "var(--elevation-sm)" } : undefined}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-semibold" style={{ color: "var(--text-1)" }}>
                    {row.name}
                  </span>
                  <span className="font-mono text-[9px]" style={{ color: "var(--text-3)" }}>
                    {row.channel}
                  </span>
                </div>
                <span
                  className="overflow-hidden text-ellipsis whitespace-nowrap text-[11px]"
                  style={{ color: "var(--text-3)" }}
                >
                  {row.preview}
                </span>
              </div>
            ))}
          </div>

          <div className="flex flex-col p-4">
            <div className="mb-3.5 flex items-center justify-between border-b pb-3" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2">
                <span
                  className="flex items-center justify-center rounded-full text-[11px] font-bold"
                  style={{ width: 26, height: 26, background: "var(--brand-subtle)", color: "var(--brand-ink)" }}
                >
                  MG
                </span>
                <div>
                  <div className="text-[12.5px] font-semibold">María González</div>
                  <div className="font-mono text-[10.5px]" style={{ color: "var(--text-3)" }}>
                    +502 5555 0182 · WhatsApp
                  </div>
                </div>
              </div>
            </div>

            <Bubble from="customer" text="Hola, hice un pedido ayer. ¿Ya está listo?" time="10:41" />
            <Bubble from="agent" text="¡Hola María! Sí, tu pedido está listo desde las 9am. Podés pasar a recogerlo hoy." time="10:42" />
            <Bubble from="customer" text="Genial, paso en un rato 🙌" time="10:43" />

            <div className="mt-auto flex items-center gap-2 pt-3">
              <div
                className="flex-1 rounded-[var(--radius-lg)] border px-3 py-2 text-xs"
                style={{ borderColor: "var(--border)", background: "var(--surface-2)", color: "var(--text-3)" }}
              >
                Escribí un mensaje…
              </div>
              <div
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[var(--radius-md)]"
                style={{ background: "var(--brand)", color: "var(--brand-on)" }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
                  <path d="M22 2 11 13" />
                  <path d="M22 2 15 22l-4-9-9-4z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* chips flotantes */}
      <div
        className="absolute flex items-center gap-2 rounded-[var(--radius-lg)] border px-3.5 py-2.5 text-xs font-semibold"
        style={{
          top: -18,
          right: 26,
          borderColor: "var(--border)",
          background: "var(--surface)",
          boxShadow: "var(--elevation-md)",
          color: "var(--status-warn-ink)",
        }}
      >
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--status-warn)" }} />
        SLA en riesgo — 2 min
      </div>
      <div
        className="absolute flex items-center gap-2 rounded-[var(--radius-lg)] border px-3.5 py-2.5 text-xs font-medium"
        style={{
          bottom: -20,
          left: 8,
          borderColor: "var(--border)",
          background: "var(--surface)",
          boxShadow: "var(--elevation-md)",
          color: "var(--text-2)",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20z" />
        </svg>
        WhatsApp · Messenger
      </div>
    </div>
  );
}
